import { mapRateSourceFromDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';
import { RateSource } from '@/modules/common/types/rate-source';
import {
  ProjectTeamMember,
  ProjectTeamMemberType,
} from '@/modules/projects/types/project-team-member';

import { TeamMemberWithRateResponse } from '../types/dto/team-member-with-rate.dto';

/**
 * Maps TeamMemberWithRateResponse from API to ProjectTeamMember domain model.
 * Converts rate options array into individual rate values based on their source.
 */
export const mapTeamMemberWithRateFromResponse = (
  response: TeamMemberWithRateResponse
): ProjectTeamMember => {
  const rateValues: Partial<ProjectTeamMember> = {};

  // Map each rate option to its corresponding field
  response.rateOptions.forEach(option => {
    switch (option.source) {
      case RateSourceDTO.USER:
        rateValues.userExternalRateValue = option.rate;
        break;
      case RateSourceDTO.SENIORITY:
        rateValues.seniorityBasedRateValue = option.rate;
        break;
      case RateSourceDTO.ROLE:
        rateValues.roleBasedRateValue = option.rate;
        break;
      case RateSourceDTO.ORGANIZATION:
        rateValues.globalDefaultRateValue = option.rate;
        break;
    }
  });

  // Determine the active rate source (default to USER if available, otherwise first available)
  const userOption = response.rateOptions.find(opt => opt.source === RateSourceDTO.USER);
  const activeRateSource = userOption
    ? RateSource.UserExternalRate
    : response.rateOptions.length > 0
      ? mapRateSourceFromDTO(response.rateOptions[0].source)
      : RateSource.UserExternalRate;

  return {
    id: String(response.user.id),
    name: response.user.name,
    email: response.user.email ?? undefined,
    avatarLink: response.user.profileImageUrl || undefined,
    department: undefined, // Not provided by this endpoint
    type: ProjectTeamMemberType.ManuallyAdded,
    ...rateValues,
    activeRateSource,
  };
};

/**
 * Maps an array of TeamMemberWithRateResponse to ProjectTeamMember array.
 */
export const mapTeamMembersWithRateFromResponse = (
  responses: TeamMemberWithRateResponse[]
): ProjectTeamMember[] => {
  return responses.map(mapTeamMemberWithRateFromResponse);
};
